import { cleanAccentsAndDiacritics } from 'apps/ids/src/app/utils/stringUtils'
import { createAndAddActionHistory, historyActionType } from '../_utils'
import { GetSavablePatientFormDataProps } from '../_props'
import { useFetchInsiAttributes } from './useFetchInsiAttributes'
import { SavablePatientValues } from './_utils'

export const useGetSavablePatientData = () => {
    const fetchInsiAttributes = useFetchInsiAttributes()

    const getSavablePatientFormData = async ({
        data,
        mode,
        currentUser,
        updatedFields,
    }: GetSavablePatientFormDataProps) => {
        const attributes = await fetchInsiAttributes(data.insiIdentityAttributes)

        const firstNameList = data.firstNameList?.reduce<string[]>((prev, curr) => {
            if (typeof curr === 'string') {
                prev.push(curr)
            } else if (curr?.__typename === 'Element') {
                prev.push(curr.value)
            }

            return prev
        }, [])

        if (!firstNameList) {
            throw new Error('Error while saving, firstNameList is undefined')
        }

        const result: SavablePatientValues = {
            firstName:
                !data.firstName || data.firstName === ''
                    ? cleanAccentsAndDiacritics(firstNameList[0]).toUpperCase()
                    : cleanAccentsAndDiacritics(data.firstName).toUpperCase(),
            lastName: cleanAccentsAndDiacritics(data.lastName ?? '').toUpperCase(),
            birthName: cleanAccentsAndDiacritics(data.birthName ?? '').toUpperCase(),
            birthNameNormalized: cleanAccentsAndDiacritics(data.birthName ?? '').toUpperCase(),
            firstFirstNameNormalized: cleanAccentsAndDiacritics(firstNameList[0] ?? '').toUpperCase(),
            firstFirstName: cleanAccentsAndDiacritics(data.firstFirstName ?? '').toUpperCase(),
            birthDate: data.birthDate,
            firstNameList: firstNameList.map((x) => cleanAccentsAndDiacritics(x).toUpperCase()),
            sex: data.sex?.toUpperCase(),
            room: data.room,
            contactPhone: data.contactPhone,
            contactEmail: data.contactEmail,
            insNum: data.insNum,
            insOid: data.insOid,
            socialSecurityNumber: data.socialSecurityNumber,
            birthCityCode: data.birthCity?.com,
            birthCityLabel: data.birthCity?.libelle,
            hasCss: data.hasCss,
            attendingPhysician: data.attendingPhysician,
            insuredSsn: data.insuredSsn,
            patientInsuredUserLabel: data.patientInsuredUserLabel,
            patientIsSameAsInsured: data.patientIsSameAsInsured,
            insiIdentityAttributes: attributes,
            countryBirth: {
                link: data.countryBirth?.id,
            },
            hasLtDiseases: data.hasLtDiseases,
            lastInsiIdentityCheckResult: data.lastInsiIdentityCheckResult,
            manualIdentityValidation: data.manualIdentityValidation,
            attachmentOrg: data.attachmentOrg,
        }

        if (!data.address?.objectId) {
            result.address = {
                createAndLink: {
                    street: data.address?.street,
                    city: data.address?.city,
                    postalCode: data.address?.postalCode,
                },
            }
        }
        if (data.identityProof?.objectId || data.identityProof?.id) {
            result.identityProof = {
                link: data.identityProof?.objectId ?? data.identityProof?.id,
            }
        }
        if (mode === 'EDITION' && data.objectId && updatedFields && updatedFields.length > 0) {
            result.actionHistory = {
                createAndAdd: [
                    createAndAddActionHistory({
                        patientId: data.objectId,
                        user: currentUser,
                        mode: historyActionType.UPDATE,
                        actionDetails: updatedFields,
                    }),
                ],
            }
        }
        return result
    }

    return getSavablePatientFormData
}
